import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import { DatePicker, Form, InputNumber, Select, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TuneIcon from "@mui/icons-material/Tune";
import { unwrapResult } from "@reduxjs/toolkit";
import locale from "antd/es/date-picker/locale/fr_FR";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import moment from "moment";

import {
  CANAL_OPTIONS_STRINGS,
  DELAI_RESOLUTION_OPTIONS_STRINGS,
  RATE_OPTIONS,
} from "../../../../constants";
import Snackbars from "../../../../GlobalComponents/Alerts/Snackbars";
import FiltreIcon from "../../../../GlobalComponents/IconComponents/FiltreIcon";
import { fetchChaines } from "../../../../ReduxTLK/features/Chaines/ChaineSlice";
import {
  fetchListPventesIntervenant,
  fetchPventes,
} from "../../../../ReduxTLK/features/Pointdeventes/PointdeventeSlice";
import {
  closeFilterBox,
  openFilterBox,
  resetFilter,
  resetFilterCounter,
  setFilterCounter,
  setFilterDraft,
  setFilterModel,
} from "../../../../ReduxTLK/features/SAV/FilterSlice";
import {
  array_machine,
  array_priorite,
  array_status,
} from "../../../../ReduxTLK/features/SAV/ticketList";
import { fetchFilteredTickets } from "../../../../ReduxTLK/features/SAV/TicketSlice";
import { ticketService } from "../../../../service/ticketService";
import RatiosCards from "../Ratios/RatiosCards";
import TicketBadges from "../TicketBadges/TicketBadges";

import styles from "./Filter.module.css";

const { RangePicker } = DatePicker;
const DATE_FORMAT = "MM-YYYY";
const HIDDEN_STATUS_IDS = [5, 7, 8];

const EMPTY_FILTER_BODY = {
  id: null,
  chaineId: null,
  pointVenteId: null,
  statut: null,
  equipement: null,
  priorite: null,
  intervenantId: null,
  rate: null,
  canal: null,
  delaiRésolution: null,
  idAssignee: null,
  dateBegin: null,
  dateEnd: null,
};

/* ---------- Option mappers (normalize everything to { label, value }) ---------- */
const toOptions = (list = [], label = "nom", value = "id") =>
  list.map((item) => ({ value: item[value], label: item[label] }));

const toFullNameOptions = (list = []) =>
  list.map((item) => ({ value: item.id, label: `${item.nom} ${item.prenom}` }));

/* ---------- Shared Select props ---------- */
const baseSelectProps = {
  allowClear: true,
  showSearch: true,
  placeholder: "Sélectionner",
  optionFilterProp: "label",
  style: { width: "100%" },
};

const multiSelectProps = {
  ...baseSelectProps,
  mode: "multiple",
  maxTagCount: "responsive",
};

export default function Filter({
  nbrsOfTickets,
  ratios,
  notes,
  openFiltreBox,
  setOpenFiltreBox,
  fetchData,
  setTicketList,
  setFilterOptions,
  dateBegin,
  dateEnd,
  setDateBegin,
  setDateEnd,
}) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const fetchFiltredModel = useSelector((state) => state.filter.filterModel);
  const savedFilterCounter = useSelector((state) => state.filter.counter);
  const savedFilterForm = useSelector((state) => state.filter.filter);
  const chaines = useSelector((state) => state.chaine.chaines);
  const agentsSAV = useSelector((state) => state.user.agentsSAV);

  const [loading, setLoading] = useState(false);
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const [message, setMessage] = useState("");
  const [pointDeVentes, setPointDeVentes] = useState([]);
  const [intervenants, setIntervenants] = useState([]);

  /* ---------- Hydrate form from saved filter ---------- */
  useEffect(() => {
    dispatch(fetchChaines());

    if (!savedFilterForm) return;
    form.setFieldsValue({
      ...savedFilterForm,
      dateCreation: savedFilterForm.dateCreation
        ?.map((d) => (d ? dayjs(d) : null))
        .filter(Boolean),
    });
    // Re-fetch dependent lists so saved selections stay readable
    if (savedFilterForm.chaineId != null) loadPointsDeVente(savedFilterForm.chaineId);
    if (savedFilterForm.pointVenteId?.length) loadIntervenants(savedFilterForm.pointVenteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Sync panel open state + page size ---------- */
  useEffect(() => {
    dispatch(openFiltreBox ? openFilterBox() : closeFilterBox());
    fetchData({
      paginationModel: {
        page: fetchFiltredModel?.paginationModel?.page,
        pageSize: openFiltreBox ? 8 : 12,
        nbPage: null,
      },
      body: { ...fetchFiltredModel.body },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFiltreBox]);

  /* ---------- Cascading data loaders ---------- */
  const loadPointsDeVente = (id) =>
    dispatch(fetchPventes(id))
      .then(unwrapResult)
      .then((res) => setPointDeVentes(res?.data?.data?.result ?? []));

  const loadIntervenants = (pvIds) =>
    dispatch(fetchListPventesIntervenant(pvIds)).then((res) =>
      setIntervenants(res?.payload?.data?.data?.result ?? [])
    );

  /* ---------- Apply / reset ---------- */
  const applyFilters = (body, { isReset = false } = {}) => {
    setLoading(true);
    dispatch(
      fetchFilteredTickets({
        paginationModel: { page: 1, pageSize: 12, nbPage: null },
        body,
      })
    )
      .then(unwrapResult)
      .then((res) => {
        const { currentPage, pageSize, nbPage } = res?.data?.pagination ?? {};
        dispatch(
          setFilterModel({
            paginationModel: { page: currentPage, pageSize, nbPage },
            body,
          })
        );
        setTicketList(res?.data?.result?.tickets);
        setOpenFiltreBox(false);
        dispatch(closeFilterBox());
        if (isReset) {
          setFilterOptions(EMPTY_FILTER_BODY);
          dispatch(resetFilter());
        }
      })
      .catch(() => {
        setMessage("Une erreur est survenue lors du chargement des tickets.");
        setOpenErrorBox(true);
      })
      .finally(() => setLoading(false));
  };

  const onFinish = (values) => {
    const body = { ...values, dateBegin, dateEnd };
    dispatch(setFilterDraft(values));
    dispatch(setFilterCounter(countActiveFilters(values)));
    applyFilters(body);
  };

  const resetForm = () => {
    dispatch(resetFilterCounter());
    form.resetFields();
    setPointDeVentes([]);
    setIntervenants([]);
    applyFilters(EMPTY_FILTER_BODY, { isReset: true });
  };

  const handleBadgeClick = (badge) => {
    onFinish({
      ...fetchFiltredModel.body,
      statut: badge.statut != null ? [badge.statut] : null,
    });
  };

  /* ---------- Field change side-effects ---------- */
  const onValuesChange = (changedValues, allValues) => {
    setFilterOptions(allValues);
    const [key, value] = Object.entries(changedValues)[0];

    switch (key) {
      case "dateCreation":
        setDateBegin(value?.[0]?.startOf("month").format("YYYY-MM-DD"));
        setDateEnd(value?.[1]?.endOf("month").format("YYYY-MM-DD"));
        break;

      case "chaineId":
        form.setFieldsValue({ pointVenteId: null, intervenantId: null });
        setPointDeVentes([]);
        setIntervenants([]);
        if (value != null) loadPointsDeVente(value);
        break;

      case "pointVenteId":
        form.setFieldsValue({ intervenantId: null });
        setIntervenants([]);
        if (value?.length) loadIntervenants(value);
        break;

      default:
        break;
    }
  };

  /* ---------- Export ---------- */
  const handleExportExcel = () => {
    setLoading(true);
    ticketService
      .exportTicketsExcel(fetchFiltredModel.body)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res?.data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = `export_ticket_${moment().format("DDMMYYYY_HHmmss")}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        setMessage("Une erreur est survenue lors de l'exportation.");
        setOpenErrorBox(true);
      })
      .finally(() => setLoading(false));
  };

  /* ---------- Helpers ---------- */
  const countActiveFilters = (obj) =>
    Object.values(obj).filter((v) =>
      Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined
    ).length;

  const disabledDate = (current) => current && current > moment().endOf("month");

  const validateIsInteger = (_, value) =>
    value && !Number.isInteger(value)
      ? Promise.reject(new Error("Veuillez saisir un entier valide"))
      : Promise.resolve();

  /* ---------- Declarative field config ---------- */
  const selectFields = useMemo(
    () => [
      {
        name: "chaineId",
        label: "Entreprise",
        options: toOptions(chaines),
        props: baseSelectProps,
      },
      {
        name: "pointVenteId",
        label: "Établissement",
        options: toOptions(pointDeVentes),
      },
      {
        name: "statut",
        label: "Statut",
        options: toOptions(
          array_status.filter((s) => !HIDDEN_STATUS_IDS.includes(s.id))
        ),
      },
      { name: "equipement", label: "Équipement", options: toOptions(array_machine) },
      { name: "priorite", label: "Priorité", options: toOptions(array_priorite) },
      {
        name: "intervenantId",
        label: "Intervenant",
        options: toFullNameOptions(intervenants),
      },
      {
        name: "idAssignee",
        label: "Agent",
        options: toFullNameOptions(agentsSAV),
      },
      { name: "rate", label: "Note", options: RATE_OPTIONS },
      { name: "canal", label: "Canal", options: CANAL_OPTIONS_STRINGS },
      {
        name: "delaiRésolution",
        label: "Estimation de la résolution",
        options: DELAI_RESOLUTION_OPTIONS_STRINGS,
      },
    ],
    [chaines, pointDeVentes, intervenants, agentsSAV]
  );

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {savedFilterCounter > 0 && (
            <span className={styles.counterPill}>
              <span className={styles.counterDot} />
              {savedFilterCounter} filtre{savedFilterCounter > 1 ? "s" : ""}{" "}
              appliqué{savedFilterCounter > 1 ? "s" : ""}
            </span>
          )}
          {ratios && <RatiosCards ratios={ratios} notes={notes} />}
        </div>

        <div className={styles.headerRight}>
          <Button
            onClick={handleExportExcel}
            startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />}
            className={`${styles.headerBtn} ${styles.exportBtn}`}
            disableElevation
          >
            Export Excel
          </Button>
          <Button
            onClick={() => setOpenFiltreBox(!openFiltreBox)}
            startIcon={<FiltreIcon />}
            className={`${styles.headerBtn} ${styles.filterToggleBtn} ${
              openFiltreBox ? styles.filterToggleBtnActive : ""
            }`}
            disableElevation
          >
            Filtres
            {savedFilterCounter > 0 && (
              <span className={styles.btnBadge}>{savedFilterCounter}</span>
            )}
          </Button>
        </div>
      </div>

      {/* Filter panel */}
      {openFiltreBox && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <div className={styles.panelTitle}>
              <span className={styles.panelTitleIcon}>
                <TuneIcon sx={{ fontSize: 16 }} />
              </span>
              Filtres avancés
            </div>
          </div>

          <Form
            form={form}
            name="control-hooks-filter"
            onValuesChange={onValuesChange}
            onFinish={onFinish}
            layout="vertical"
          >
            <div className={styles.filterGrid}>
              {/* Référence */}
              <div className={styles.filterField}>
                <label className={styles.fieldLabel}>Référence</label>
                <Form.Item
                  name="id"
                  rules={[{ validator: validateIsInteger }]}
                  className={styles.formItem}
                >
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              </div>

              {/* All Select fields, config-driven */}
              {selectFields.map(({ name, label, options, props }) => (
                <div className={styles.filterField} key={name}>
                  <label className={styles.fieldLabel}>{label}</label>
                  <Form.Item name={name} className={styles.formItem}>
                    <Select {...(props ?? multiSelectProps)} options={options} />
                  </Form.Item>
                </div>
              ))}

              {/* Date */}
              <div className={styles.filterField}>
                <label className={styles.fieldLabel}>Date</label>
                <Form.Item name="dateCreation" className={styles.formItem}>
                  <RangePicker
                    allowClear
                    disabledDate={disabledDate}
                    picker="month"
                    size="middle"
                    placeholder={[DATE_FORMAT, DATE_FORMAT]}
                    locale={locale}
                    format={DATE_FORMAT}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>
            </div>

            <div className={styles.actionsRow}>
              <Button className={styles.resetBtn} onClick={resetForm} disabled={loading}>
                Réinitialiser
              </Button>
              <Button className={styles.confirmBtn} type="submit" disabled={loading}>
                {loading ? (
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 18, color: "#fff" }} spin />
                    }
                  />
                ) : (
                  "Appliquer les filtres"
                )}
              </Button>
            </div>
          </Form>
        </div>
      )}

      <Snackbars
        severity="error"
        message={message}
        open={openErrorBox}
        setOpen={setOpenErrorBox}
        autohideduration={1000}
      />

      <TicketBadges nbrsOfTickets={nbrsOfTickets} onBadgeClick={handleBadgeClick} />
    </div>
  );
}